import { useEffect, useState } from "react";
import LOGO_SRC from "@/assets/img/logo.webp";
// Kept short so the branded intro does not hold back Speed Index / LCP. The
// heavy third-party embeds no longer block `load`, so the cap is rarely reached.
const MIN_MS = 300;
const MAX_MS = 900;

interface Props {
  onDone?: () => void;
}

const PremiumLoader = ({ onDone }: Props) => {
  const [hiding, setHiding] = useState(false);
  const [removed, setRemoved] = useState(false);

  useEffect(() => {
    const start = performance.now();
    let hideTimer: ReturnType<typeof setTimeout>;
    let removeTimer: ReturnType<typeof setTimeout>;

    const hide = () => {
      const elapsed = performance.now() - start;
      const wait = Math.max(MIN_MS - elapsed, 0);
      hideTimer = setTimeout(() => {
        setHiding(true);
        removeTimer = setTimeout(() => {
          setRemoved(true);
          onDone?.();
        }, 450);
      }, wait);
    };

    const maxTimer = setTimeout(hide, MAX_MS);
    if (document.readyState === "complete") {
      hide();
    } else {
      window.addEventListener("load", hide, { once: true });
    }

    return () => {
      clearTimeout(hideTimer);
      clearTimeout(removeTimer);
      clearTimeout(maxTimer);
      window.removeEventListener("load", hide);
    };
  }, [onDone]);

  if (removed) return null;

  return (
    <div
      aria-hidden={hiding}
      role="status"
      aria-live="polite"
      className={`fixed inset-0 z-[10000] flex items-center justify-center transition-opacity duration-500 ${
        hiding ? "opacity-0 pointer-events-none" : "opacity-100"
      }`}
      style={{
        background:
          "radial-gradient(ellipse at center, hsl(var(--accent)) 0%, hsl(var(--accent-dark)) 75%)",
      }}
    >
      <div className="relative flex flex-col items-center px-6">
        <div
          className="absolute -inset-16 rounded-full opacity-60 blur-3xl motion-safe:animate-pulse"
          style={{
            background:
              "radial-gradient(circle, hsl(var(--primary) / 0.55) 0%, transparent 70%)",
          }}
          aria-hidden="true"
        />
        <div className="relative flex items-center justify-center mb-6">
          <div
            className="absolute inset-0 -m-4 rounded-full border-2 border-transparent motion-safe:animate-spin"
            style={{
              borderTopColor: "hsl(var(--primary))",
              borderRightColor: "hsl(var(--primary) / 0.4)",
              animationDuration: "1.4s",
            }}
            aria-hidden="true"
          />
          <img
            src={LOGO_SRC}
            alt=""
            aria-hidden="true"
            className="relative h-12 w-auto motion-safe:animate-[fade-in_0.6s_ease-out]"
          />
        </div>
        <div className="relative h-[2px] w-44 overflow-hidden rounded-full bg-white/10">
          <div
            className="absolute inset-y-0 left-0 w-1/3 rounded-full"
            style={{
              background:
                "linear-gradient(90deg, transparent, hsl(var(--primary)), transparent)",
              animation: "premium-loader-bar 1.2s ease-in-out infinite",
            }}
          />
        </div>
        <p className="relative mt-5 text-xs tracking-[0.25em] uppercase text-white/70">
          Preparing your experience
        </p>
      </div>
      <style>{`
        @keyframes premium-loader-bar {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }
        @media (prefers-reduced-motion: reduce) {
          [role="status"] * { animation: none !important; transition: none !important; }
        }
      `}</style>
    </div>
  );
};

export default PremiumLoader;